'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IMG } from '../../imgPrefix';

export interface ComboOption {
  /** Stable key. */
  id: string;
  /** What goes into the input when this option is chosen. */
  value: string;
  /** Primary line — the place or attraction name on its own. */
  label: string;
  /**
   * Muted second line — "Greater London, United Kingdom".
   *
   * Location rows only. Attraction rows deliberately leave it unset: BCP asks
   * for the attraction NAME alone, with no price, rating or count (the old
   * dropdown showed all three).
   */
  hint?: string;
  /** Icon-chip key — see `ICON_CONFIG`. Defaults to a generic map pin. */
  type?: 'city' | 'attraction';
  /**
   * The attraction's own photo, shown in place of the icon chip.
   *
   * Attraction rows only. Absent, blank, or a URL that fails to load all fall
   * back to the same `no-image.png` placeholder the Shows cards use, so a
   * missing photo is never a broken-image glyph.
   */
  image?: string;
}

/**
 * Icon chip per row type, mirroring the hotels destination dropdown: a tinted
 * rounded square with a Font Awesome glyph (loaded globally in `app/layout.tsx`,
 * so nothing extra is imported here).
 */
const ICON_CONFIG: Record<string, { icon: string; cls: string }> = {
  city: { icon: 'fa-city', cls: 'city' },
  attraction: { icon: 'fa-ticket', cls: 'attraction' },
};

const ICON_FALLBACK = { icon: 'fa-map-marker-alt', cls: 'default' };

/**
 * The same "No Image" placeholder the Shows cards use
 * (`app/_components/nearby/cards/NearbyShowCard.tsx`), resolved the same way:
 * a blank source falls back immediately, a relative path is prefixed with the
 * image host, and a URL that 404s swaps to the placeholder on error.
 */
const FALLBACK_IMAGE = `${IMG}/image/no-image.png`;

function resolveSuggestionImage(src: string | undefined): string {
  if (!src) return FALLBACK_IMAGE;
  return src.startsWith('http') ? src : `${IMG}${src}`;
}

/**
 * Attraction thumbnail in the icon-chip slot.
 *
 * Decorative — the row's own label names the attraction, so an alt text here
 * would just be read twice.
 */
function SuggestionImage({ src }: { src?: string }) {
  const [currentSrc, setCurrentSrc] = useState(resolveSuggestionImage(src));

  useEffect(() => {
    setCurrentSrc(resolveSuggestionImage(src));
  }, [src]);

  return (
    <img
      className="atr-combo__thumb"
      src={currentSrc}
      alt=""
      aria-hidden="true"
      loading="lazy"
      /*
       * Intrinsic size, not only a CSS rule.
       *
       * The chip is a fixed 36px box but does not clip by default, so an <img>
       * with no size applied paints at its NATURAL dimensions and spills across
       * the whole row — the dropdown reads as a big cover image rather than an
       * icon. These attributes hold the 36px square even if the stylesheet is
       * stale or a rule is ever dropped, which is exactly how that regression
       * reached the browser.
       */
      width={36}
      height={36}
      // Guarded so a failing placeholder cannot loop.
      onError={() => {
        if (currentSrc !== FALLBACK_IMAGE) setCurrentSrc(FALLBACK_IMAGE);
      }}
    />
  );
}

/**
 * Bolds the typed substring inside a suggestion label, as the hotels dropdown
 * does, so the reason a row matched is visible at a glance.
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q || !text) return text;
  const index = text.toLowerCase().indexOf(q.toLowerCase());
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <span className="atr-combo__hl">{text.slice(index, index + q.length)}</span>
      {text.slice(index + q.length)}
    </>
  );
}

interface SearchComboboxProps {
  id: string;
  placeholder: string;
  /** Accessible name — the visible label is hidden in the hero pill. */
  ariaLabel: string;
  icon: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  /**
   * Options for the current input. The parent owns fetching/filtering so this
   * component works for both a preloaded list (cities) and a live remote search
   * (attractions) without knowing which it is.
   */
  options: ComboOption[];
  loading?: boolean;
  /**
   * Chosen from the menu — by click, Enter or Tab. Returning `true` means the
   * parent navigated away, so the menu stays closed and the input is untouched.
   */
  onSelect?: (option: ComboOption) => boolean | void;
  /** Shown when the menu is open, not loading, and there are no options. */
  emptyLabel?: string;
  /**
   * Characters required before the menu may open at all — no panel, no
   * "No results found", nothing (BCP-2055 §4, BCP-2056 §4).
   *
   * It belongs here rather than in the parent because the parent can only
   * withhold OPTIONS; the menu would still open on focus and render the
   * empty-state note, which the specs' "typing 1 or 2 characters shows no
   * suggestion panel" AC forbids. Both fields pass the same minimum, so the
   * rule is configured once per field rather than reimplemented twice.
   */
  minChars?: number;
  /** Submits the surrounding form; also fires on Enter with nothing highlighted. */
  onEnterWithoutSelection?: () => void;
  /**
   * Blocks typing entirely, for a field whose prerequisite is unmet.
   *
   * Implemented as `readOnly` rather than `disabled` ON PURPOSE: a `disabled`
   * input fires no events at all, so it cannot tell the parent that someone
   * TRIED to use it — and the whole point here is to explain the dependency at
   * the moment of the attempt. `readOnly` still focuses and still clicks, so
   * `onBlockedInteraction` fires, while the value stays uneditable.
   */
  blocked?: boolean;
  /** Fired when a blocked field is clicked, focused or typed into. */
  onBlockedInteraction?: () => void;
  /** Marks the field invalid and wires it to an error message element. */
  invalid?: boolean;
  describedBy?: string;
  /**
   * Validation message, rendered INSIDE this field beneath its input.
   *
   * Presentation only — the caller still owns when it appears and what it says.
   * It lives here rather than under the whole search pill so the message sits
   * with the field it is about, which is what the design shows.
   */
  error?: string;
  /** Element id for the message, so `aria-describedby` can point at it. */
  errorId?: string;
  /**
   * Clears the field (×). The button shows only while the field holds text,
   * like the Sports and Shows search inputs. The parent owns the reset,
   * because what else a clear resets (a pinned destination, stored city)
   * differs per field.
   */
  onClear?: () => void;
  /** Accessible name for the × button. */
  clearLabel?: string;
}

/**
 * Input + dropdown used by both fields of the attractions hero search.
 *
 * Deliberately hand-rolled rather than pulling in a combobox library: the two
 * fields hit different endpoints (destinations vs products) but must look and
 * behave identically, and this is small enough that a dependency would cost
 * more than it saves.
 *
 * Rows follow the hotels destination dropdown — icon chip, name with the typed
 * substring highlighted, muted sub-line — so the two verticals read the same
 * (BCP-2055 rule 4). The markup and styling are the attractions module's own;
 * nothing is imported from the hotels domain, which route isolation forbids.
 *
 * Keyboard: ↑/↓ move, Enter takes the highlighted option (or submits when none
 * is highlighted), Escape closes, Tab commits the highlight then moves on.
 */
export function SearchCombobox({
  id,
  placeholder,
  ariaLabel,
  icon,
  value,
  onChange,
  options,
  loading = false,
  onSelect,
  emptyLabel = 'No results found',
  minChars = 0,
  onEnterWithoutSelection,
  blocked = false,
  onBlockedInteraction,
  invalid = false,
  describedBy,
  error,
  errorId,
  onClear,
  clearLabel = 'Clear search',
}: SearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = `${useId()}-listbox`;

  /**
   * The menu is PORTALLED to <body> and positioned fixed.
   *
   * It cannot render inside the field: the hero is `.atr-hero.atr-ticket`, which
   * sets BOTH `overflow: hidden` and a `mask-image` for its notched ticket
   * shape. Either one clips a descendant, so an absolutely-positioned menu got
   * sliced off at the hero's bottom edge. A portal escapes both.
   */
  const measure = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom + 10, left: r.left, width: r.width + 101 });
  }, []);

  // useEffect, not useLayoutEffect: client components are server-rendered, and
  // useLayoutEffect warns there. Nothing paints unmeasured anyway — the menu
  // is gated on `rect`.
  useEffect(() => {
    if (!open) return;
    measure();
    // `true` captures scrolls on any ancestor, not just the window.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  // Close on any click outside the field — pointerdown rather than click so the
  // menu is gone before a click on the page behind it resolves.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      // The menu is portalled to <body>, so it is NOT inside wrapRef — test it
      // separately or the first click on an option would close the menu first.
      if (wrapRef.current?.contains(t) || listRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  // A changed result set invalidates the highlight — keeping the old index
  // would point at a different option than the one under the cursor.
  useEffect(() => setActive(-1), [options]);

  // Keep the highlighted row in view when arrowing past the visible window.
  useEffect(() => {
    if (active < 0) return;
    listRef.current?.querySelectorAll('li')[active]?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const longEnough = value.trim().length >= minChars;
  // × only while there is text to clear, as in the Sports/Shows search input.
  const showClear = Boolean(onClear && value && !blocked);
  const showMenu =
    !blocked && open && longEnough && (loading || options.length > 0 || Boolean(value.trim()));

  const choose = (option: ComboOption) => {
    const navigated = onSelect?.(option);
    setOpen(false);
    setActive(-1);
    if (navigated !== true) onChange(option.value);
    // A selection that opened a new tab must also drop focus: the browser
    // re-fires `focus` on this input when the visitor returns to the tab, and
    // `onFocus` would reopen the menu.
    else inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (blocked) {
      // Tab and the modifier combos still work — trapping those would break
      // keyboard navigation of the rest of the form.
      if (e.key === 'Tab' || e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      onBlockedInteraction?.();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (!options.length) return;
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i + delta + options.length) % options.length);
      return;
    }
    if (e.key === 'Enter') {
      // `showMenu`, not `open`: below the character minimum there is no visible
      // list, so Enter must submit the form rather than silently commit a
      // highlight left over from a longer query.
      if (showMenu && active >= 0 && options[active]) {
        e.preventDefault();
        choose(options[active]);
      } else {
        onEnterWithoutSelection?.();
      }
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
      return;
    }
    if (e.key === 'Tab' && showMenu && active >= 0 && options[active]) {
      choose(options[active]);
    }
  };

  return (
    <div className="atr-searchbar__field atr-combo" ref={wrapRef}>
      {icon}
      <div className="atr-combo__body">
        <label className="atr-visually-hidden" htmlFor={id}>
          {ariaLabel}
        </label>
        {/* Input + × share a positioning box so the × centres on the INPUT,
            not on the whole field, which grows when the error line shows. */}
        <div className={`atr-combo__control${showClear ? ' has-clear' : ''}`}>
          <input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            aria-expanded={showMenu}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
            placeholder={placeholder}
            value={value}
            readOnly={blocked}
            aria-disabled={blocked || undefined}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            className={blocked ? 'is-blocked' : undefined}
            onChange={(e) => {
              if (blocked) {
                onBlockedInteraction?.();
                return;
              }
              onChange(e.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              if (blocked) {
                onBlockedInteraction?.();
                return;
              }
              setOpen(true);
            }}
            // A click on an already-focused blocked field fires no focus event, so
            // a second attempt would silently do nothing without this.
            onMouseDown={() => {
              if (blocked) onBlockedInteraction?.();
            }}
            onKeyDown={onKeyDown}
            autoComplete="off"
          />
          {showClear && (
            // Same button as the Sports/Shows search input (`.search-clear-btn`,
            // styles/searchbar.css): absolutely placed at the field's right.
            <button
              type="button"
              className="search-clear-btn atr-combo__clear"
              onClick={() => {
                onClear?.();
                setOpen(false);
                setActive(-1);
              }}
              aria-label={clearLabel}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}
        </div>

        {error && (
          <span className="atr-combo__error" id={errorId} role="alert">
            {error}
          </span>
        )}
      </div>

      {showMenu &&
        rect &&
        createPortal(
          <ul
            className="atr-combo__menu"
            id={listId}
            role="listbox"
            ref={listRef}
            style={{ top: rect.top, left: rect.left, width: rect.width }}
          >
            {loading && <li className="atr-combo__note">Searching…</li>}

            {!loading && options.length === 0 && <li className="atr-combo__note">{emptyLabel}</li>}

            {options.map((o, i) => {
              const cfg = ICON_CONFIG[o.type ?? ''] ?? ICON_FALLBACK;
              return (
                <li
                  key={o.id}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === active}
                  className={`atr-combo__opt atr-combo__opt--${o.type ?? 'default'}${
                    i === active ? ' is-active' : ''
                  }`}
                  // mousedown, not click: the input's blur would otherwise tear the
                  // menu down before the click lands.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(o);
                  }}
                  onMouseEnter={() => setActive(i)}
                >
                  {/* An attraction row shows the product's own photo; every
                      other row keeps the tinted glyph chip. */}
                  <span
                    className={`atr-combo__icon atr-combo__icon--${
                      o.type === 'attraction' ? 'photo' : cfg.cls
                    }`}
                    aria-hidden="true"
                  >
                    {o.type === 'attraction' ? (
                      <SuggestionImage src={o.image} />
                    ) : (
                      <i className={`fas ${cfg.icon}`} />
                    )}
                  </span>
                  <span className="atr-combo__text">
                    <span className="atr-combo__label">{highlightMatch(o.label, value)}</span>
                    {o.hint && <span className="atr-combo__hint">{o.hint}</span>}
                  </span>
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </div>
  );
}
